import { Injectable } from '@angular/core';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { Store } from '@ngrx/store';
import {
  RewardsProgram,
  RewardsProgramPreference,
} from '@rd/types/rewards_program.type';
import { getElementBy } from '@rd/utils/getElementBy';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  CreateRewardsProgramPreference,
  GetRewardsProgramPreference,
} from '../store/actions/rewards-program-preference.actions';
import {
  getRewardsPrograms,
  getRewardsProgramsPreference,
  getRewardsProgramsPreferenceLoading,
  getRewardsProgramsPreferenceLoadingFailed,
  getRewardsProgramsPreferenceLoadingSuccess,
} from '../store/selectors/rewards-program.selector';

@Injectable({ providedIn: 'root' })
export class RewardsProgramsFacade {
  constructor(
    private store: Store,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
  ) {}

  rewardsProgramPreferenceLoading$ = this.store.select(
    getRewardsProgramsPreferenceLoading,
  );

  rewardsProgramPreference$: Observable<RewardsProgramPreference> =
    this.store.select(getRewardsProgramsPreference);

  rewardsProgramPreferenceError$: Observable<boolean> = this.store.select(
    getRewardsProgramsPreferenceLoadingFailed,
  );

  rewardsProgramPreferenceFailed$: Observable<boolean> =
    this.rewardsProgramPreferenceLoading$.pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.rewardsProgramPreferenceError$),
    );

  rewardsPrograms$: Observable<Record<string, RewardsProgram>> =
    this.store.select(getRewardsPrograms);

  selectedRewardsProgram$: Observable<RewardsProgram | null> = combineLatest([
    this.loyaltyProgramFacade.loadLoyaltyProgramsSuccess$,
    this.store.select(getRewardsProgramsPreferenceLoadingSuccess),
  ]).pipe(
    filter((v) => v.every(Boolean)),
    switchMap((v) =>
      combineLatest([
        this.rewardsProgramPreference$,
        this.rewardsPrograms$,
      ]).pipe(
        map(
          ([rewardsProgramsPreference, rewardsPrograms]) =>
            Object.values(rewardsPrograms).find(
              (rp) => rp.id === rewardsProgramsPreference.rewardsProgramId,
            ) ?? null,
        ),
      ),
    ),
  );

  getRewardsProgramById(id: string): Observable<RewardsProgram> {
    return this.store.select(getRewardsPrograms).pipe(
      map((rp) => Object.values(rp)),
      getElementBy(id),
      filter(isNotNull),
    );
  }

  createRewardsProgramPreference(rewardsProgramId: string): void {
    this.store.dispatch(CreateRewardsProgramPreference({ rewardsProgramId }));
  }

  loadRewardsProgramPreference(): void {
    this.store.dispatch(GetRewardsProgramPreference());
  }
}
